import React from "react"
import { FormattedMessage } from "react-intl"
import { Link as RouterLink } from "react-router-dom"
import { Link, Typography, UnorderedList, UnorderedListItem } from "@carrotfertility/carotene-core"
import { Subsidy } from "#/lib/carrot-api/types/Subsidy"
import { DbgPaths } from "#/utils/Paths"
import { PlanInfoDialog, PlanInfoModalType } from "#/components/views/planinfo/PlanInfoBadge"
import { TileAccordion } from "#/components/views/molecules/TileAccordion"
import { usePlanInfo } from "./PlanInfoBadgeContainer"

const taxesLink = (linkContent: React.ReactNode) => (
  <Link component={RouterLink} to={DbgPaths.TAXES}>
    {linkContent}
  </Link>
)

export const YourResponsibilityCard = ({
  subsidy,
  allSubsidies
}: {
  subsidy: Subsidy
  allSubsidies: Subsidy[]
}): JSX.Element => {
  const { showDeductible, showCostShare, deductibleModalType, includesOopm } = usePlanInfo(subsidy, allSubsidies)
  const [openModal, setOpenModal] = React.useState<PlanInfoModalType | null>(null)
  const title = <FormattedMessage defaultMessage="Your responsibility" />

  const id = `your-responsibility-${subsidy.reimbursementCategoryNameMessageKey}`

  if (!showDeductible && !showCostShare) {
    return (
      <TileAccordion id={id} color="green" title={title} collapsible={false}>
        <Typography variant="body2">
          <FormattedMessage
            defaultMessage="Carrot funds you use may be <link>taxed as additional income</link>. If applicable, these taxes may significantly reduce the size of your paycheck."
            values={{ link: taxesLink }}
          />
        </Typography>
      </TileAccordion>
    )
  }

  const dialogLink = (modalType: PlanInfoModalType, label: React.ReactNode) => (
    <UnorderedListItem>
      <Link component="button" onClick={() => setOpenModal(modalType)}>
        {label}
      </Link>
    </UnorderedListItem>
  )

  return (
    <TileAccordion
      id={`your-responsibility-${subsidy.reimbursementCategoryNameMessageKey}`}
      color="green"
      title={title}
      summary={(expanded) => (
        <Typography variant="body2">
          {expanded ? (
            <FormattedMessage defaultMessage="You may need to cover a portion of eligible expenses:" />
          ) : (
            <FormattedMessage defaultMessage="You may need to cover a portion of eligible expenses." />
          )}
        </Typography>
      )}
    >
      <UnorderedList>
        {showDeductible &&
          dialogLink(
            deductibleModalType,
            includesOopm ? (
              <FormattedMessage defaultMessage="Deductible and out-of-pocket maximum" />
            ) : (
              <FormattedMessage defaultMessage="Deductible" />
            )
          )}
        {showCostShare && dialogLink("costShare", <FormattedMessage defaultMessage="Cost share" />)}
        <UnorderedListItem>
          <Link component={RouterLink} to={DbgPaths.TAXES}>
            <FormattedMessage defaultMessage="Taxes" />
          </Link>
        </UnorderedListItem>
      </UnorderedList>
      {/* Inside the tile so dialog clicks don't bubble to a surrounding accordion. */}
      {openModal && <PlanInfoDialog subsidy={subsidy} modalType={openModal} onClose={() => setOpenModal(null)} />}
    </TileAccordion>
  )
}
